import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, X, ChevronLeft, ChevronRight, SlidersHorizontal, ChevronDown } from "lucide-react";
import api from "./api";

const PAGE = 25;
const STATUS_ORDER = [
  "Contact Became Client",
  "Client Planning Attended",
  "Contact Committed",
  "Data Collection Complete",
  "New Lead",
  "Lost Interest / Rejected",
  "Bad Contact Information",
];
const STATUS_COLORS = {
  "Contact Became Client":     "#30d158", // green
  "Client Planning Attended":  "#0071e3", // blue
  "Contact Committed":         "#af52de", // purple
  "Data Collection Complete":  "#ff9f0a", // amber
  "New Lead":                  "#8e8e93", // grey
  "Lost Interest / Rejected":  "#ff453a", // red
  "Bad Contact Information":   "#636366", // dark grey
};

const SEGMENTS = [
  { id: "all",           label: "All",              params: {} },
  { id: "became_client", label: "Client",           params: { status: "Contact Became Client" } },
  { id: "planning",      label: "Planning",         params: { status: "Client Planning Attended" } },
  { id: "committed",     label: "Committed",        params: { status: "Contact Committed" } },
  { id: "data_complete", label: "Data Complete",    params: { status: "Data Collection Complete" } },
  { id: "new_lead",      label: "New Lead",         params: { status: "New Lead" } },
];

const SORT_OPTIONS = [
  ["date_arrived:desc",   "Date arrived (newest)"],
  ["date_arrived:asc",    "Date arrived (oldest)"],
  ["agent:asc",           "Assigned advisor (A→Z)"],
  ["agent:desc",          "Assigned advisor (Z→A)"],
  ["policy_agent:asc",    "Policy advisor (A→Z)"],
  ["policy_agent:desc",   "Policy advisor (Z→A)"],
  ["annual_premium:desc", "Annual premium (high)"],
  ["annual_premium:asc",  "Annual premium (low)"],
  ["name:asc",            "Name (A→Z)"],
  ["name:desc",           "Name (Z→A)"],
];

export default function Contacts() {
  const [segment, setSegment] = useState("all");
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [province, setProvince] = useState("");
  const [sortKey, setSortKey] = useState("date_arrived:desc");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [lookups, setLookups] = useState({ statuses: [], stages: [], sources: [], provinces: [] });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const nav = useNavigate();

  useEffect(() => { api.get("/crm/lookups").then((r) => setLookups(r.data)); }, []);
  useEffect(() => { const t = setTimeout(() => setQDeb(q), 250); return () => clearTimeout(t); }, [q]);

  const segParams = useMemo(() => SEGMENTS.find((s) => s.id === segment)?.params || {}, [segment]);
  const [sort, order] = sortKey.split(":");

  useEffect(() => {
    setLoading(true);
    const isGrouped = segment === "all" && !status && !source && !province && !qDeb;
    const params = {
      q: qDeb || undefined,
      status: status || undefined,
      source: source || undefined,
      province: province || undefined,
      sort, order,
      skip: isGrouped ? 0 : page * PAGE,
      limit: isGrouped ? 2000 : PAGE,
      ...segParams,
    };
    api.get("/crm/contacts", { params }).then((r) => { setData(r.data); setLoading(false); });
  }, [qDeb, status, source, province, sort, order, page, segParams, segment]);

  useEffect(() => setPage(0), [qDeb, status, source, province, segParams, sortKey]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE));

  return (
    <div data-testid="contacts-page">
      {/* Header */}
      <header className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="text-[34px] tracking-tight leading-none" style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.02em" }}>
            Contacts
          </h1>
          <div className="mt-2 text-[14px]" style={{ color: "var(--crm-text-muted)" }}>
            {data.total.toLocaleString()} records
          </div>
        </div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="new-contact-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium text-black"
          style={{ background: "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)" }}
        >
          <Plus size={14} /> New client
        </button>
      </header>

      {/* Segment tabs */}
      <div
        className="inline-flex p-1 rounded-full mb-4"
        style={{ background: "var(--crm-hover)", border: "1px solid var(--crm-line)" }}
        data-testid="segment-tabs"
      >
        {SEGMENTS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSegment(s.id)}
            data-testid={`segment-${s.id}`}
            className="px-4 py-1.5 rounded-full text-[13px] transition"
            style={{
              background: segment === s.id ? "var(--crm-bg-elev)" : "transparent",
              color: segment === s.id ? "var(--crm-text)" : "var(--crm-text-muted)",
              fontWeight: segment === s.id ? 600 : 400,
              boxShadow: segment === s.id ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Search + Sort + Filter toggle */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--crm-text-subtle)" }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone, ID…"
            data-testid="contacts-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg text-[13px] focus:outline-none"
            style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }}
          />
        </div>
        <Select
          value={sortKey}
          onChange={setSortKey}
          options={SORT_OPTIONS}
          testid="sort-select"
          minWidth={160}
        />
        <button
          onClick={() => setShowFilters((v) => !v)}
          data-testid="toggle-filters"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px]"
          style={{
            background: showFilters ? "var(--crm-accent-soft)" : "var(--crm-bg-elev)",
            border: "1px solid var(--crm-line)",
            color: showFilters ? "var(--crm-accent)" : "var(--crm-text)",
          }}
        >
          <SlidersHorizontal size={14} /> Filters
          {(status || source || province) && (
            <span className="ml-1 w-1.5 h-1.5 rounded-full" style={{ background: "var(--crm-accent)" }} />
          )}
        </button>
      </div>

      {/* Expandable filter row */}
      {showFilters && (
        <div
          className="flex flex-wrap gap-3 mb-4 p-4 rounded-2xl"
          style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)" }}
          data-testid="filters-panel"
        >
          <Select value={province} onChange={setProvince} placeholder="All provinces"
            options={(lookups.provinces || []).map((p) => [p, p])} testid="filter-province" minWidth={140} />
          <Select value={source} onChange={setSource} placeholder="All sources"
            options={(lookups.sources || []).map((s) => [s, s])} testid="filter-source" minWidth={160} />
          <Select value={status} onChange={setStatus} placeholder="All statuses"
            options={(lookups.statuses || []).map((s) => [s, s])} testid="filter-status" minWidth={160} />
          {(status || source || province) && (
            <button
              onClick={() => { setStatus(""); setSource(""); setProvince(""); }}
              className="text-[12px] px-3 py-2 rounded-lg"
              style={{ color: "var(--crm-text-muted)" }}
              data-testid="clear-filters"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table — grouped by stage when segment=all, flat table otherwise */}
      {segment === "all" && !status && !source && !province && !qDeb
        ? <GroupedByStage items={data.items} nav={nav} loading={loading} />
        : <FlatTable items={data.items} nav={nav} loading={loading} />}

      {/* Pagination — hidden when grouped by stage */}
      {!(segment === "all" && !status && !source && !province && !qDeb) && (
        <div className="flex items-center justify-between mt-4 text-[13px]" style={{ color: "var(--crm-text-muted)" }}>
        <div>Page {page + 1} of {totalPages}</div>
        <div className="flex gap-2">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-40"
            style={{ border: "1px solid var(--crm-line)", background: "var(--crm-bg-elev)", color: "var(--crm-text)" }}
            data-testid="pagination-prev"
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <button
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-40"
            style={{ border: "1px solid var(--crm-line)", background: "var(--crm-bg-elev)", color: "var(--crm-text)" }}
            data-testid="pagination-next"
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>
      )}

      {showNew && (
        <NewContactModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => { setShowNew(false); nav(`/crm/contacts/${id}`); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Row({ c, nav }) {
  return (
    <tr
      className="transition cursor-pointer"
      onClick={() => nav(`/crm/contacts/${c.contact_id}`)}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--crm-hover)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      data-testid={`contact-row-${c.contact_id}`}
      style={{ borderBottom: "1px solid var(--crm-line-soft)" }}
    >
      <Td>
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] shrink-0"
            style={{ background: "var(--crm-accent-soft)", color: "var(--crm-accent)", fontWeight: 600 }}
          >
            {(c.first_name?.[0] || c.name?.[0] || "?").toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="truncate" style={{ color: "var(--crm-text)", fontWeight: 500 }}>{c.name}</div>
            <div className="text-[11.5px] truncate" style={{ color: "var(--crm-text-subtle)" }}>{c.demographic || c.contact_id}</div>
          </div>
        </div>
      </Td>
      <Td className="hidden md:table-cell" style={{ color: "var(--crm-text-muted)" }}>
        <span className="truncate block max-w-[220px]">{c.email || "—"}</span>
      </Td>
      <Td className="hidden lg:table-cell" style={{ color: "var(--crm-text-muted)" }}>{c.phone || "—"}</Td>
      <Td className="hidden md:table-cell text-[12px]" style={{ color: "var(--crm-text-muted)" }}>{c.status || "—"}</Td>
      <Td className="hidden lg:table-cell text-[12px]" style={{ color: "var(--crm-text-muted)" }}>
        {c.date_arrived ? new Date(c.date_arrived).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—"}
      </Td>
      <Td className="hidden lg:table-cell text-[12px]" style={{ color: "var(--crm-text-muted)" }}>
        <AgentPill name={c.agent} />
      </Td>
      <Td className="hidden xl:table-cell text-[12px]" style={{ color: "var(--crm-text-muted)" }}>
        <AgentPill name={c.policy_agent} muted />
      </Td>
      <Td className="hidden xl:table-cell text-right tabular-nums" style={{ color: "var(--crm-text)" }}>
        {c.annual_premium || c.deal_size ? `$${Math.round(c.annual_premium || c.deal_size).toLocaleString()}` : "—"}
      </Td>
    </tr>
  );
}

function AgentPill({ name, muted }) {
  if (!name) return <span style={{ color: "var(--crm-text-subtle)" }}>—</span>;
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  // Deterministic hue per name so Biagio/Christian get distinct dots
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  const dot = muted ? "#c7c7cc" : `hsl(${hue}, 55%, 55%)`;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dot }} />
      <span className="truncate max-w-[140px]" title={name}>
        {initials ? `${initials} · ${name.split(" ")[0]}` : name}
      </span>
    </span>
  );
}

function TableHead() {
  return (
    <thead>
      <tr style={{ borderBottom: "1px solid var(--crm-line-soft)" }}>
        <Th>Name</Th>
        <Th className="hidden md:table-cell">Email</Th>
        <Th className="hidden lg:table-cell">Phone</Th>
        <Th className="hidden md:table-cell">Status</Th>
        <Th className="hidden lg:table-cell">Date arrived</Th>
        <Th className="hidden lg:table-cell">Assigned</Th>
        <Th className="hidden xl:table-cell">Policy advisor</Th>
        <Th className="hidden xl:table-cell text-right">Annual premium</Th>
      </tr>
    </thead>
  );
}

function FlatTable({ items, nav, loading }) {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)", boxShadow: "var(--crm-shadow)" }}>
      <table className="w-full text-[13px]">
        <TableHead />
        <tbody>
          {loading ? (
            <tr><td colSpan={8} className="p-8 text-center" style={{ color: "var(--crm-text-subtle)" }}>Loading…</td></tr>
          ) : items.length === 0 ? (
            <tr><td colSpan={8} className="p-8 text-center" style={{ color: "var(--crm-text-subtle)" }}>No contacts match your filters.</td></tr>
          ) : (
            items.map((c) => <Row key={c.id} c={c} nav={nav} />)
          )}
        </tbody>
      </table>
    </div>
  );
}

function GroupedByStage({ items, nav, loading }) {
  const [expanded, setExpanded] = useState(() =>
    Object.fromEntries(STATUS_ORDER.map((s) => [s, !["New Lead", "Lost Interest / Rejected", "Bad Contact Information"].includes(s)])),
  );
  if (loading) {
    return (
      <div className="rounded-2xl p-8 text-center" style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)", color: "var(--crm-text-subtle)" }}>
        Loading {items.length ? items.length : ""} records…
      </div>
    );
  }
  const buckets = Object.fromEntries(STATUS_ORDER.map((s) => [s, []]));
  buckets.Other = [];
  for (const c of items) {
    const key = c.status && buckets[c.status] ? c.status : "Other";
    buckets[key].push(c);
  }
  const statuses = [...STATUS_ORDER, ...(buckets.Other.length ? ["Other"] : [])];
  return (
    <div className="space-y-6">
      {statuses.map((stat) => {
        const rows = buckets[stat];
        if (!rows.length) return null;
        const open = expanded[stat];
        const preview = 15;
        const visible = open ? rows : rows.slice(0, preview);
        const slug = stat.toLowerCase().replace(/[^a-z]+/g, "-");
        return (
          <section key={stat} data-testid={`status-group-${slug}`}>
            <button
              onClick={() => setExpanded({ ...expanded, [stat]: !open })}
              className="w-full flex items-center justify-between gap-3 mb-3 group text-left"
              data-testid={`status-toggle-${slug}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[stat] || "#8e8e93" }} />
                <span className="text-[15px]" style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.01em" }}>
                  {stat}
                </span>
                <span className="text-[12px] tabular-nums px-2 py-0.5 rounded-full" style={{ background: "var(--crm-hover)", color: "var(--crm-text-muted)" }}>
                  {rows.length.toLocaleString()}
                </span>
              </div>
              <ChevronDown
                size={16}
                style={{
                  color: "var(--crm-text-muted)",
                  transform: open ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.2s",
                }}
              />
            </button>
            <div className="rounded-2xl overflow-hidden" style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)", boxShadow: "var(--crm-shadow)" }}>
              <table className="w-full text-[13px]">
                <TableHead />
                <tbody>
                  {visible.map((c) => <Row key={c.id} c={c} nav={nav} />)}
                </tbody>
              </table>
              {!open && rows.length > preview && (
                <button
                  onClick={() => setExpanded({ ...expanded, [stat]: true })}
                  className="w-full py-3 text-[12.5px] transition"
                  style={{ color: "var(--crm-accent)", borderTop: "1px solid var(--crm-line-soft)" }}
                  data-testid={`status-showall-${slug}`}
                >
                  Show all {rows.length.toLocaleString()} in {stat} →
                </button>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Select({ value, onChange, placeholder, options, testid, minWidth = 130 }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="px-3 py-2 rounded-lg text-[13px] focus:outline-none"
      style={{
        background: "var(--crm-bg-elev)",
        border: "1px solid var(--crm-line)",
        color: "var(--crm-text)",
        minWidth,
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
    </select>
  );
}

function StageDot({ text }) {
  if (!text) return <span style={{ color: "var(--crm-text-subtle)" }}>—</span>;
  const t = text.toLowerCase();
  let color = "#8e8e93";
  if (t.includes("client closed") || t.includes("client")) color = "#30d158";
  else if (t.includes("discovery completed")) color = "#0071e3";
  else if (t.includes("discovery booked")) color = "#ff9f0a";
  else if (t.includes("active")) color = "#af52de";
  else if (t.includes("raw")) color = "#8e8e93";
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px]" style={{ color: "var(--crm-text)" }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {text}
    </span>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left text-[10.5px] uppercase tracking-[0.08em] font-normal ${className}`} style={{ color: "var(--crm-text-subtle)" }}>{children}</th>
);
const Td = ({ children, className = "", ...rest }) => (
  <td className={`px-4 py-3 align-middle ${className}`} {...rest}>{children}</td>
);

// ---------------------------------------------------------------------------
function NewContactModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", type: "prospect", stage: "Raw Lead",
    lead_source: "", deal_size: "", province: "ON",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const body = { ...form, deal_size: form.deal_size ? Number(form.deal_size) : null };
      const { data } = await api.post("/crm/contacts", body);
      onCreated(data.contact_id);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="new-contact-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl p-8"
        style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)", boxShadow: "0 30px 60px rgba(0,0,0,0.2)" }}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-[22px]" style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.01em" }}>
              Add a client or prospect
            </h2>
            <div className="text-[13px] mt-1" style={{ color: "var(--crm-text-muted)" }}>Minimal record — build the deep profile after.</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ color: "var(--crm-text-muted)" }}>
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Fld label="Full name" required v={form.name} onChange={upd("name")} testid="new-name" />
          <Fld label="Email" type="email" v={form.email} onChange={upd("email")} testid="new-email" />
          <Fld label="Phone" v={form.phone} onChange={upd("phone")} testid="new-phone" />
          <Fld label="Province" v={form.province} onChange={upd("province")} testid="new-province" />
          <Sel label="Type" v={form.type} onChange={upd("type")} testid="new-type"
            options={[["prospect", "Prospect"], ["contact", "Contact"], ["client", "Client"]]} />
          <Sel label="Stage" v={form.stage} onChange={upd("stage")} testid="new-stage"
            options={[["Raw Lead", "Raw Lead"], ["Active Lead", "Active Lead"], ["Discovery Booked", "Discovery Booked"], ["Discovery Completed", "Discovery Completed"], ["Client Closed", "Client Closed"]]} />
          <Fld label="Lead source" v={form.lead_source} onChange={upd("lead_source")} testid="new-source" />
          <Fld label="Annual premium" type="number" v={form.deal_size} onChange={upd("deal_size")} testid="new-deal" />
        </div>

        {error && (
          <div className="mt-4 text-[13px] rounded-xl px-4 py-2.5"
            style={{ background: "rgba(255,69,58,0.08)", border: "1px solid rgba(255,69,58,0.2)", color: "#ff453a" }}
            data-testid="new-contact-error">
            {error}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-[13px]"
            style={{ border: "1px solid var(--crm-line)", color: "var(--crm-text)" }}>
            Cancel
          </button>
          <button type="submit" disabled={busy || !form.name.trim()}
            data-testid="new-contact-submit"
            className="px-4 py-2 rounded-lg text-[13px] font-medium text-black disabled:opacity-50"
            style={{ background: "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)" }}>
            {busy ? "Creating…" : "Create record"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Fld({ label, v, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest mb-1.5 block" style={{ color: "var(--crm-text-subtle)" }}>{label}</span>
      <input
        type={type} required={required} value={v} onChange={onChange} data-testid={testid}
        className="w-full px-3 py-2.5 rounded-lg text-[13px] focus:outline-none"
        style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }}
      />
    </label>
  );
}

function Sel({ label, v, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest mb-1.5 block" style={{ color: "var(--crm-text-subtle)" }}>{label}</span>
      <select value={v} onChange={onChange} data-testid={testid}
        className="w-full px-3 py-2.5 rounded-lg text-[13px] focus:outline-none"
        style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }}>
        {options.map(([val, lbl]) => (<option key={val} value={val}>{lbl}</option>))}
      </select>
    </label>
  );
}
